import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/apiBaseUrl';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

const attachAuthHeader = async (config, { forceRefresh = false } = {}) => {
  await auth.authStateReady();
  const user = auth.currentUser;

  if (!user) {
    return config;
  }

  try {
    const token = await user.getIdToken(forceRefresh);
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error('Error getting auth token:', error);
  }

  return config;
};

axiosInstance.interceptors.request.use(
  async (config) => attachAuthHeader(config),
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      await attachAuthHeader(originalRequest, { forceRefresh: true });
      return axiosInstance(originalRequest);
    }

    if (error.response?.status === 401) {
      const requestUrl = originalRequest?.url || '';
      const isPartnerLoginRequest = requestUrl.includes('/admin/partner-login');
      const isPartnerSetPasswordRequest = /\/admin\/partners\/[^/]+\/set-password/.test(requestUrl);

      // Public activation endpoint — keep user on the page so the UI can toast the error.
      if (!isPartnerSetPasswordRequest) {
        void (async () => {
          try {
            const { teardownFcmWebRegistration } = await import('./fcmWebRegistration');
            await teardownFcmWebRegistration();
          } catch (teardownError) {
            console.warn('[FCM] Teardown before 401 sign-out failed:', teardownError);
          }
          await auth.signOut();
        })();

        const loginPath = isPartnerLoginRequest ? '/partner-login' : '/login';
        if (!window.location.pathname.startsWith(loginPath)) {
          window.location.href = loginPath;
        }
      }
    }

    return Promise.reject((error.response && error.response.data) || error);
  },
);

export default axiosInstance;
